import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { faVolume } from '@fortawesome/pro-solid-svg-icons/faVolume';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import connect from 'components/lib/connect';
import DropdownMenu from 'components/common/dropdown_menu_v2';
import { DEFAULT_CHANNEL_SOUNDS, SHARED_SOUND_OPTIONS } from 'actions/notification/lib/notification_constants';
import { EndpointTypes } from 'models/endpoint';
import i18n from 'scripts/i18n/i18n';
import playNotificationSound from 'actions/notification/lib/play_notification_sound';
import Toggle from 'components/common/toggle';
import ToggleCustomizeChannelNotificationSounds from 'actions/agent_preferences/toggle_customize_channel_notification_sounds';
import UpdateChannelNotificationSound from 'actions/agent_preferences/update_channel_notification_sound';
import { useExecuteAction } from 'components/hooks/connect_hooks';

const SOUND_OFF = '';

const CHANNEL_LABELS = Object.freeze({
  [EndpointTypes.CHAT]: 'Chat',
  [EndpointTypes.CUSTOM_CHANNEL]: 'Custom Channel',
  [EndpointTypes.INSTAGRAM_COMMENT]: 'Instagram Comment',
  [EndpointTypes.INSTAGRAM_DIRECT]: 'Instagram Direct',
  [EndpointTypes.SMS]: 'SMS',
  [EndpointTypes.FB_MESSENGER]: 'Facebook Messenger',
  [EndpointTypes.WHATSAPP]: 'WhatsApp',
  [EndpointTypes.TWITTER]: 'Twitter',
});

// EMAIL and VOICE are deliberately omitted: their routing events flow
// through non-messaging paths (RoutingChannel.MAIL), which the observer
// gates with isMessagingType — so a per-channel choice for them would
// never actually fire on a real notification.
const CHANNELS_IN_ORDER = Object.freeze([
  EndpointTypes.CHAT,
  EndpointTypes.SMS,
  EndpointTypes.FB_MESSENGER,
  EndpointTypes.WHATSAPP,
  EndpointTypes.INSTAGRAM_DIRECT,
  EndpointTypes.INSTAGRAM_COMMENT,
  EndpointTypes.TWITTER,
  EndpointTypes.CUSTOM_CHANNEL,
]);

const CHANNEL_OPTIONS = Object.freeze([...SHARED_SOUND_OPTIONS, { value: SOUND_OFF, label: 'No sound' }]);

export function ChannelNotificationSounds({ channelSounds, isCustomizeEnabled }) {
  const executeAction = useExecuteAction();
  const onSelect = useCallback(
    (channel, type) => {
      executeAction(UpdateChannelNotificationSound, { channel, type });
    },
    [executeAction]
  );

  const onPreview = useCallback((channel, currentValue) => {
    // Preview whatever the user has currently selected; fall back to the
    // channel default if they've picked "No sound".
    playNotificationSound(currentValue || DEFAULT_CHANNEL_SOUNDS[channel]);
  }, []);

  const onToggle = useCallback(() => {
    executeAction(ToggleCustomizeChannelNotificationSounds);
  }, [executeAction]);

  return (
    <div>
      <HeaderRow>
        <div className="agentProfile-header">{i18n.t('Channel Notification Sounds')}</div>
        <Toggle data-aid="customizeChannelSoundsToggle" isEnabled={isCustomizeEnabled} onChange={onToggle} />
      </HeaderRow>

      {isCustomizeEnabled
        ? CHANNELS_IN_ORDER.map(channel => {
            const savedValue = channel in channelSounds ? channelSounds[channel] : DEFAULT_CHANNEL_SOUNDS[channel];
            // Saved prefs may contain a sound type that has since been removed
            // (e.g. TRIPLE_DING). Fall back to the channel default in that case
            // so DropdownMenu can render — otherwise its ButtonText returns
            // undefined and React throws.
            const isValid = CHANNEL_OPTIONS.some(o => o.value === savedValue);
            const currentValue = isValid ? savedValue : DEFAULT_CHANNEL_SOUNDS[channel];
            const label = CHANNEL_LABELS[channel];
            return (
              <ChannelRow key={channel}>
                <ChannelLabel>{label}</ChannelLabel>
                <PreviewButton
                  aria-label={`Preview ${label} sound`}
                  onClick={() => onPreview(channel, currentValue)}
                  title={`Preview ${label} sound`}
                  type="button"
                >
                  <FontAwesomeIcon icon={faVolume} />
                </PreviewButton>
                <DropdownWrapper>
                  <DropdownMenu
                    allowEmptyValues
                    onSelect={type => onSelect(channel, type)}
                    options={CHANNEL_OPTIONS}
                    value={currentValue || SOUND_OFF}
                  />
                </DropdownWrapper>
              </ChannelRow>
            );
          })
        : null}
    </div>
  );
}

ChannelNotificationSounds.propTypes = {
  channelSounds: PropTypes.object.isRequired,
  isCustomizeEnabled: PropTypes.bool.isRequired,
};

const ChannelNotificationSoundsContainer = connect(mapStateToProps)(ChannelNotificationSounds);

function mapStateToProps({ getProvider }) {
  const preferences = getProvider('agentPreferences').get();

  return {
    channelSounds: preferences.channelNotificationSounds || {},
    isCustomizeEnabled: !!preferences.customizeChannelNotificationSounds,
  };
}

const HeaderRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 12px;

  .agentProfile-header {
    flex: 1;
  }
`;

const ChannelRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const ChannelLabel = styled.div`
  flex: 1;
`;

const DropdownWrapper = styled.div`
  width: 160px;

  > * {
    width: 100%;
  }
`;

const PreviewButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--color-gray-light);
  cursor: pointer;
  display: flex;
  height: 24px;
  justify-content: center;
  padding: 0;
  width: 24px;

  &:hover {
    background: var(--color-gray-lighter);
    color: var(--color-text);
  }
`;

export default ChannelNotificationSoundsContainer;
